import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import ColumnChooserModal from "./ColumnChooserModal";
import TaskEditorModal from "./TaskEditorModal";
import { useFeedback } from "../../../contexts/FeedbackContext";
import { usePlanner } from "../../../contexts/PlannerContext";

/* ------------ helpers ------------ */
const twoWord = (s = "") => s.trim().split(/\s+/).slice(0, 2).join(" ");
const toYMD = (d) => (d instanceof Date ? d.toISOString().slice(0, 10) : d);
const dayMs = 86400000;
function debounce(fn, wait = 250) {
  let t;
  return (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), wait);
  };
}
const d = (v) => (v instanceof Date ? v : new Date(v));

/* ------------ constants ------------ */
const SAVED_VIEWS_KEY = "mudoSavedGanttViews";
const SELECTED_PROJECTS_KEY = "mudoGanttSelectedProjects";
const SELECTED_COLUMNS_KEY = "mudoGanttSelectedColumns";
const VIEWMODE_KEY = "mudoGanttViewMode";
const RANGE_KEY = "mudoGanttRangePreset";
const COMPLETE_COLOR = "#16a34a";

const ALL_COLUMNS = [
  { id: "_pk",        label: "ID",           width: 80,  field: "TaskID",      isPrimaryKey: true, visible: false },
  { id: "TaskName",   label: "Title",        width: 280, field: "TaskName" },
  { id: "Assignees",  label: "Assignee",     width: 160, field: "Assignees" },
  { id: "ShortName",  label: "Short Label",  width: 140, field: "ShortName" },
  { id: "StartDate",  label: "Start",        width: 130, field: "StartDate" },
  { id: "EndDate",    label: "End",          width: 130, field: "EndDate" },
  { id: "Duration",   label: "Duration",     width: 100, field: "Duration" },
  { id: "Progress",   label: "Progress %",   width: 110, field: "Progress" },
  { id: "Batch",      label: "Batch",        width: 130, field: "Batch" },
  { id: "Kiln",       label: "Kiln",         width: 130, field: "Kiln" },
  { id: "Predecessor",label: "Predecessor",  width: 150, field: "Predecessor" },
  { id: "Notes",      label: "Notes",        width: 220, field: "Notes" },
];

const DEFAULT_COLUMN_IDS = [
  "_pk",
  "TaskName",
  "Assignees",
  "ShortName",
  "StartDate",
  "EndDate",
  "Progress",
  "Predecessor",
];

/* =================================== */
export default function GanttView({
  tasks = [],
  setTasks,
  settings = {},
  projects = [],
  onBack,
  openEventId = null,
  onDeepLinkHandled,
}) {
  const navigate = useNavigate();
  const isDark = settings?.isDarkMode;
  const { play, buzz } = useFeedback();

  /* ---------- Planner bridge ---------- */
  const planner = (() => {
    try {
      return usePlanner();
    } catch {
      return null;
    }
  })();
  const usingPlanner = !!planner;

  const plannerTasks = useMemo(() => {
    if (!usingPlanner) return null;
    return (planner.events || []).map((ev) => {
      const start = ev.start ? d(ev.start) : undefined;
      const end = ev.end ? d(ev.end) : undefined;
      const duration =
        start && end
          ? Math.max(1, Math.round((end - start) / dayMs) + 1)
          : undefined;
      const stageTag = (ev.tags || []).find((t) =>
        [
          "forming",
          "drying",
          "bisque",
          "glazing",
          "glazeFiring",
          "qcPack",
          "dispatch",
        ].includes(t)
      );
      return {
        id: String(ev.id),
        title: ev.title || "(Untitled)",
        shortName: twoWord(ev.title || ""),
        startDate: start ? toYMD(start) : undefined,
        endDate: end ? toYMD(end) : undefined,
        duration,
        progress: ev.progress ?? (ev.status === "done" ? 100 : 0),
        color: ev.color || "#f97316",
        type: ev.type || "task",
        dependencies: (ev.dependencies || []).map(String),
        batchId: ev.batch || "",
        kiln:
          ev.resource === "kiln" ? ev.kiln || "kiln" : ev.kiln || "",
        assignees: ev.assignees || (ev.assigneeId ? [ev.assigneeId] : []),
        notes: ev.notes || "",
        projectId: ev.orderId || "",
        projectName: ev.orderId || "",
        stageTag,
      };
    });
  }, [usingPlanner, planner?.events]);

  const applyPlannerPatch = useCallback(
    (id, patch) => {
      if (!usingPlanner) return;
      const existing = (planner.events || []).find(
        (e) => String(e.id) === String(id)
      );
      if (!existing) return;
      const evPatch = { ...existing };
      if (patch.title != null) evPatch.title = patch.title;
      if (patch.startDate != null)
        evPatch.start = new Date(patch.startDate).toISOString();
      if (patch.endDate != null)
        evPatch.end = new Date(patch.endDate).toISOString();
      if (patch.duration != null && evPatch.start && !patch.endDate) {
        const start = d(evPatch.start);
        evPatch.end = new Date(
          start.getTime() + (patch.duration - 1) * dayMs
        ).toISOString();
      }
      if (patch.progress != null) evPatch.progress = patch.progress;
      if (patch.batchId != null)
        evPatch.batch = patch.batchId || undefined;
      if (patch.kiln != null) evPatch.kiln = patch.kiln;
      if (patch.assignees != null)
        evPatch.assignees = patch.assignees;
      if (patch.notes != null) evPatch.notes = patch.notes;
      if (patch.dependencies != null)
        evPatch.dependencies = patch.dependencies?.map(String);
      planner.upsertEvents([evPatch]);
    },
    [usingPlanner, planner]
  );

  /* ------------ UI state ------------ */
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem(VIEWMODE_KEY) || "Month";
    } catch {
      return "Month";
    }
  });
  const [showColumnsModal, setShowColumnsModal] = useState(false);
  const [savedViews, setSavedViews] = useState(() => {
    try {
      return JSON.parse(
        localStorage.getItem(SAVED_VIEWS_KEY) || "[]"
      );
    } catch {
      return [];
    }
  });
  const [selectedProjectIds, setSelectedProjectIds] = useState(() => {
    try {
      return JSON.parse(
        localStorage.getItem(SELECTED_PROJECTS_KEY) || "[]"
      );
    } catch {
      return [];
    }
  });
  const [selectedColumnIds, setSelectedColumnIds] = useState(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem(SELECTED_COLUMNS_KEY) || "[]"
      );
      return saved.length
        ? saved.includes("_pk")
          ? saved
          : ["_pk", ...saved]
        : DEFAULT_COLUMN_IDS;
    } catch {
      return DEFAULT_COLUMN_IDS;
    }
  });

  const [fastMode, setFastMode] = useState(false);
  const [highlightWeekends, setHighlightWeekends] = useState(true);

  const today = useMemo(() => new Date(), []);
  const [rangePreset, setRangePreset] = useState(() => {
    try {
      return localStorage.getItem(RANGE_KEY) || "180";
    } catch {
      return "180";
    }
  });

  const windowStart = useMemo(
    () =>
      rangePreset === "ALL"
        ? new Date(1970, 0, 1)
        : new Date(today.getTime() - 30 * dayMs),
    [rangePreset, today]
  );
  const windowEnd = useMemo(
    () =>
      rangePreset === "ALL"
        ? new Date(2099, 11, 31)
        : new Date(today.getTime() + Number(rangePreset) * dayMs),
    [rangePreset, today]
  );

  const [linkType, setLinkType] = useState("FS");
  const [groupBy, setGroupBy] = useState("none");

  /* ------------ scope & filter ------------ */
  const baseTasks = usingPlanner ? plannerTasks || [] : tasks;

  const projectOptions = useMemo(() => {
    if (projects?.length)
      return projects.map((p) => ({
        value: p.id,
        label: p.name || p.id,
        color: p.color,
      }));
    const map = new Map();
    baseTasks.forEach((t) => {
      if (t.projectId && !map.has(t.projectId)) {
        map.set(t.projectId, {
          value: t.projectId,
          label: t.projectName || t.projectId,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [projects, baseTasks]);

  const scopedTasks = useMemo(() => {
    if (!selectedProjectIds?.length) return baseTasks;
    const set = new Set(selectedProjectIds);
    return baseTasks.filter((t) => set.has(t.projectId));
  }, [baseTasks, selectedProjectIds]);

  const windowedTasks = useMemo(() => {
    const ws = windowStart.getTime(),
      we = windowEnd.getTime();
    return scopedTasks.filter((t) => {
      const s = t.startDate ? d(t.startDate).getTime() : NaN;
      const e = t.endDate
        ? d(t.endDate).getTime()
        : t.duration
        ? s + (t.duration - 1) * dayMs
        : NaN;
      if (isNaN(s) && isNaN(e)) return true;
      const start = isNaN(s) ? e : s;
      const end = isNaN(e) ? s : e;
      return end >= ws && start <= we;
    });
  }, [scopedTasks, windowStart, windowEnd]);

  /* ------------ map → rows ------------ */
  const [dataSource, setDataSource] = useState([]);
  const mapVersion = useRef(0);

  const buildRowsAsync = useCallback(
    (srcTasks, grouping) => {
      const v = ++mapVersion.current;
      const run = () => {
        if (v !== mapVersion.current) return;
        function toRow(t) {
          const predecessors =
            (t.dependencies || [])
              .map(
                (id) =>
                  `${id}${t.predecessorType || linkType || "FS"}`
              )
              .join(", ") || null;
          return {
            TaskID: String(t.id),
            TaskName: t.title,
            ShortName: t.shortName || twoWord(t.title),
            StartDate: t.startDate ? d(t.startDate) : undefined,
            EndDate: t.endDate ? d(t.endDate) : undefined,
            Duration: t.duration || undefined,
            Progress: t.progress ?? 0,
            Color: t.color || "#f97316",
            TaskType: t.type || "task",
            Predecessor: predecessors,
            Batch: t.batchId || "",
            Kiln: t.kiln || t.kilnId || "",
            Assignees: (t.assignees || []).join(", "),
            Notes: t.notes || "",
            ProjectId: t.projectId || "",
            isSummary: false,
          };
        }
        if (grouping === "none") {
          setDataSource(srcTasks.map(toRow));
        } else {
          const buckets = new Map();
          const keyOf = (t) =>
            grouping === "project"
              ? t.projectId || "Unassigned"
              : t.batchId || "Unbatched";
          srcTasks.forEach((t) => {
            const k = keyOf(t);
            if (!buckets.has(k)) buckets.set(k, []);
            buckets.get(k).push(t);
          });
          const rows = Array.from(buckets.entries()).map(
            ([k, arr]) => ({
              TaskID: `grp-${grouping}-${k}`,
              TaskName:
                grouping === "project" ? `Project: ${k}` : `Batch: ${k}`,
              isSummary: true,
              subtasks: arr.map(toRow),
            })
          );
          setDataSource(rows);
        }
      };
      if ("requestIdleCallback" in window) {
        // @ts-ignore
        requestIdleCallback(run, { timeout: 100 });
      } else {
        setTimeout(run, 0);
      }
    },
    [linkType]
  );

  useEffect(
    () => {
      buildRowsAsync(windowedTasks, groupBy);
    },
    [windowedTasks, groupBy, buildRowsAsync]
  );

  /* ------------ persist UI prefs ------------ */
  useEffect(() => {
    try {
      localStorage.setItem(
        SELECTED_PROJECTS_KEY,
        JSON.stringify(selectedProjectIds)
      );
    } catch {}
  }, [selectedProjectIds]);
  useEffect(() => {
    try {
      localStorage.setItem(
        SELECTED_COLUMNS_KEY,
        JSON.stringify(selectedColumnIds)
      );
    } catch {}
  }, [selectedColumnIds]);
  useEffect(() => {
    try {
      localStorage.setItem(VIEWMODE_KEY, viewMode);
    } catch {}
  }, [viewMode]);
  useEffect(() => {
    try {
      localStorage.setItem(RANGE_KEY, rangePreset);
    } catch {}
  }, [rangePreset]);

  const selectedColumns = useMemo(() => {
    const byId = new Map(ALL_COLUMNS.map((c) => [c.id, c]));
    return selectedColumnIds.map((id) => byId.get(id)).filter(Boolean);
  }, [selectedColumnIds]);

  /* ------------ debounced commits ------------ */
  const pendingRef = useRef({});

  const commitChanges = useCallback(
    debounce(() => {
      const pending = pendingRef.current;
      const ids = Object.keys(pending || {});
      if (!pending || !ids.length) return;

      if (usingPlanner) {
        ids.forEach((id) => {
          applyPlannerPatch(id, pending[id]);
        });
      } else if (setTasks) {
        setTasks((prev) => {
          const next = prev.map((t) =>
            pending[t.id] ? { ...t, ...pending[t.id] } : t
          );
          try {
            localStorage.setItem("mudoTasks", JSON.stringify(next));
          } catch {}
          return next;
        });
      }

      pendingRef.current = {};
      play("success");
      buzz("success");
    }, 250),
    [setTasks, play, buzz, usingPlanner, applyPlannerPatch]
  );

  /* ---------- modal wiring ---------- */
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!openEventId || !planner) return;
    const match = (planner.events || []).find((ev) => String(ev.id) === String(openEventId));
    if (match) {
      setEditing(match);
      onDeepLinkHandled?.(true);
    } else if (planner?.events?.length >= 0) {
      onDeepLinkHandled?.(false);
    }
  }, [openEventId, planner, onDeepLinkHandled]);

  function rowToEvent(row) {
    if (!row) return null;
    return {
      id: String(row.TaskID),
      title: row.TaskName,
      start: row.StartDate
        ? d(row.StartDate).toISOString()
        : undefined,
      end: row.EndDate ? d(row.EndDate).toISOString() : undefined,
      category: "",
      assigneeId:
        (row.Assignees || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)[0] || "",
      batch: row.Batch || "",
      color: row.Color || "#f97316",
      notes: row.Notes || "",
    };
  }

  const availableColumnOptions = ALL_COLUMNS.map((c) => ({
    value: c.id,
    label: c.label,
  }));

  return (
    <div
      className="min-h-screen p-6"
      style={{
        background: isDark ? "#0d0f12" : "#fff",
        color: isDark ? "#fff" : "#000",
      }}
    >
      {/* Theme patches for custom gantt */}
      <style>{`
        .mudo-gantt { font-size: 12px; }
        .mudo-gantt .outline-card { background: rgba(15,23,42,0.03); }
        .dark .mudo-gantt .outline-card { background: rgba(15,23,42,0.6); }
        .mudo-gantt .timeline-header-cell { border-bottom: 1px solid rgba(15,23,42,0.16); }
        .dark .mudo-gantt .timeline-header-cell { border-color: rgba(255,255,255,0.16); }
        .mudo-gantt .timeline-cell { border-bottom: 1px solid rgba(15,23,42,0.08); }
        .dark .mudo-gantt .timeline-cell { border-color: rgba(255,255,255,0.08); }
        .mudo-gantt .timeline-weekend { background: rgba(148,163,184,0.18); }
        .dark .mudo-gantt .timeline-weekend { background: rgba(148,163,184,0.22); }
        .mudo-gantt .timeline-bar { border-radius: 4px; color:#fff; display:flex; align-items:center; justify-content:center; font-size:11px; padding-inline:4px; cursor:pointer; }
        .mudo-gantt .timeline-row:hover .timeline-cell { background: rgba(148,163,184,0.06); }
      `}</style>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-bold">Gantt (Planner)</h2>

          {/* Project filter */}
          <div style={{ minWidth: 280, width: "min(360px, 92vw)", maxWidth: "100%" }}>
            <Select
              options={projectOptions}
              isMulti
              placeholder="Filter by projects…"
              value={projectOptions.filter((o) =>
                selectedProjectIds.includes(o.value)
              )}
              onChange={(vals) =>
                setSelectedProjectIds(vals.map((v) => v.value))
              }
              classNamePrefix="mudo"
            />
          </div>

          {/* Saved views */}
          <select
            className="border rounded px-2 py-1 text-sm"
            defaultValue=""
            onChange={(e) => {
              const v = savedViews.find((x) => x.id === e.target.value);
              if (!v) return;
              setSelectedProjectIds(v.projectIds || []);
              setViewMode(v.viewMode || "Month");
              if (v.rangePreset) setRangePreset(v.rangePreset);
              play("tap");
            }}
          >
            <option value="" disabled>
              Saved views…
            </option>
            {savedViews.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
          <button
            className="px-2 py-1 border rounded text-sm"
            onClick={() => {
              const name = prompt(
                "Name this view (projects + zoom + window):"
              );
              if (!name) return;
              const view = {
                id: String(Date.now()),
                name,
                projectIds: selectedProjectIds,
                viewMode,
                rangePreset,
                createdAt: new Date().toISOString(),
              };
              const next = [...savedViews, view];
              setSavedViews(next);
              try {
                localStorage.setItem(
                  SAVED_VIEWS_KEY,
                  JSON.stringify(next)
                );
              } catch {}
              play("success");
              buzz("success");
            }}
          >
            Save View
          </button>

          {/* Zoom scale */}
          <div className="flex items-center gap-2 ml-2">
            {["Day", "Week", "Month", "Year"].map((s) => (
              <button
                key={s}
                onClick={() => setViewMode(s)}
                className={`px-3 py-1 rounded text-sm border ${
                  viewMode === s ? "bg-orange-500 text-white" : ""
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Window presets */}
          <div className="flex items-center gap-2 ml-2">
            <span className="text-sm opacity-70">Window</span>
            {["30", "90", "180", "365", "ALL"].map((r) => (
              <button
                key={r}
                onClick={() => setRangePreset(r)}
                className={`px-2 py-1 rounded text-xs border ${
                  rangePreset === r ? "bg-orange-500 text-white" : ""
                }`}
                title={
                  r === "ALL" ? "Show all" : `Next ${r} days`
                }
              >
                {r === "ALL" ? "All" : `${r}d`}
              </button>
            ))}
          </div>

          {/* Link type */}
          <div className="flex items-center gap-1 ml-2">
            {["FS", "SS", "FF", "SF"].map((t) => (
              <button
                key={t}
                onClick={() => setLinkType(t)}
                className={`px-2 py-1 rounded text-xs border ${
                  linkType === t ? "bg-orange-500 text-white" : ""
                }`}
                title={
                  {
                    FS: "Finish-to-Start",
                    SS: "Start-to-Start",
                    FF: "Finish-to-Finish",
                    SF: "Start-to-Finish",
                  }[t]
                }
              >
                {t}
              </button>
            ))}
          </div>

          {/* Grouping */}
          <div className="flex items-center gap-1 ml-3">
            <span className="text-sm opacity-75">Group by</span>
            {["none", "project", "batch"].map((g) => (
              <button
                key={g}
                onClick={() => setGroupBy(g)}
                className={`px-2 py-1 rounded text-xs border capitalize ${
                  groupBy === g ? "bg-orange-500 text-white" : ""
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          {/* Columns */}
          <button
            className="px-3 py-1 rounded border text-sm ml-2"
            onClick={() => setShowColumnsModal(true)}
            title="Choose columns for the outline (left table)"
          >
            Columns
          </button>
        </div>

        {/* Right header actions */}
        <div className="flex items-center gap-2">
          <label className="text-sm flex items-center gap-2 opacity-80">
            <input
              type="checkbox"
              checked={fastMode}
              onChange={(e) => setFastMode(e.target.checked)}
            />
            Fast mode
          </label>
          <label className="text-sm flex items-center gap-2 opacity-80">
            <input
              type="checkbox"
              checked={highlightWeekends}
              onChange={(e) =>
                setHighlightWeekends(e.target.checked)
              }
              disabled={fastMode}
            />
            Weekend shading
          </label>

          {onBack ? (
            <button
              onClick={onBack}
              className="px-3 py-1 rounded border"
            >
              Back
            </button>
          ) : (
            <button
              onClick={() => navigate("/dashboard")}
              className="px-3 py-1 rounded border"
            >
              Back
            </button>
          )}
        </div>
      </div>

      {/* Gantt area: outline + timeline side-by-side */}
      <div className="border rounded overflow-hidden mudo-gantt">
        <div className="flex max-h-[72vh]">
          {/* Outline / left panel */}
          <div className="w-[320px] max-w-[50%] border-r overflow-y-auto">
            <OutlinePanel
              rows={dataSource}
              selectedColumns={selectedColumns}
              onPatch={(id, patch) => {
                pendingRef.current[id] = {
                  ...(pendingRef.current[id] || {}),
                  ...patch,
                };
                commitChanges();
              }}
              onOpen={(row) => setEditing(rowToEvent(row))}
            />
          </div>

          {/* Timeline / right panel */}
          <div className="flex-1 overflow-x-auto">
            <PlannerTimeline
              rows={dataSource}
              viewMode={viewMode}
              rangePreset={rangePreset}
              highlightWeekends={!fastMode && highlightWeekends}
              fastMode={fastMode}
              onOpen={(row) => setEditing(rowToEvent(row))}
            />
          </div>
        </div>
      </div>

      {showColumnsModal && (
        <ColumnChooserModal
          allColumns={availableColumnOptions}
          selectedIds={selectedColumnIds}
          onClose={() => setShowColumnsModal(false)}
          onApply={(ids) => {
            setSelectedColumnIds(
              ids.includes("_pk") ? ids : ["_pk", ...ids]
            );
            setShowColumnsModal(false);
            play("tap");
          }}
        />
      )}

      <TaskEditorModal
        open={!!editing}
        event={editing || undefined}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

/* --------- Pure React timeline --------- */
function PlannerTimeline({
  rows,
  viewMode,
  rangePreset,
  highlightWeekends,
  fastMode,
  onOpen,
}) {
  const today = useMemo(() => new Date(), []);
  const baseDayMs = 86400000;

  const windowStart = useMemo(
    () =>
      rangePreset === "ALL"
        ? new Date(1970, 0, 1)
        : new Date(today.getTime() - 30 * baseDayMs),
    [rangePreset, today]
  );
  const windowEnd = useMemo(
    () =>
      rangePreset === "ALL"
        ? new Date(2099, 11, 31)
        : new Date(today.getTime() + Number(rangePreset) * baseDayMs),
    [rangePreset, today]
  );

  const ticks = useMemo(() => {
    const arr = [];
    const start = new Date(
      windowStart.getFullYear(),
      windowStart.getMonth(),
      windowStart.getDate()
    );
    const end = new Date(
      windowEnd.getFullYear(),
      windowEnd.getMonth(),
      windowEnd.getDate()
    );
    for (let t = start.getTime(); t <= end.getTime(); t += baseDayMs) {
      arr.push(new Date(t));
    }
    return arr;
  }, [windowStart, windowEnd]);

  const baseWidth =
    viewMode === "Day"
      ? 96
      : viewMode === "Week"
      ? 64
      : viewMode === "Month"
      ? 32
      : 24;

  const colTemplate = useMemo(
    () => `repeat(${ticks.length}, minmax(${baseWidth}px, 1fr))`,
    [ticks.length, baseWidth]
  );

  const idxOf = useCallback(
    (date) => {
      if (!date) return -1;
      const base = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      ).getTime();
      for (let i = 0; i < ticks.length; i++) {
        if (ticks[i].getTime() === base) return i;
      }
      return -1;
    },
    [ticks]
  );

  const flatRows = useMemo(() => {
    // flatten groups so each actual task row is drawn
    const out = [];
    rows.forEach((row) => {
      if (row.isSummary && row.subtasks) {
        out.push({
          ...row,
          isSummaryHeader: true,
        });
        row.subtasks.forEach((st) => out.push(st));
      } else {
        out.push(row);
      }
    });
    return out;
  }, [rows]);

  return (
    <div className="mudo-timeline">
      {/* Header */}
      <div
        className="timeline-header grid sticky top-0 z-10 bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur-sm"
        style={{ gridTemplateColumns: colTemplate }}
      >
        {ticks.map((d, i) => {
          const isWeekend = [0, 6].includes(d.getDay());
          const label = `${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][
            d.getDay()
          ]} ${String(d.getDate()).padStart(2, "0")}`;
          return (
            <div
              key={i}
              className={`timeline-header-cell px-2 py-1 text-xs whitespace-nowrap ${
                highlightWeekends && isWeekend ? "timeline-weekend" : ""
              }`}
            >
              {label}
            </div>
          );
        })}
      </div>

      {/* Body */}
      <div>
        {flatRows.map((row) => {
          if (row.isSummaryHeader) {
            return (
              <div
                key={row.TaskID}
                className="timeline-row flex items-center px-2 py-1 border-b text-xs font-semibold bg-slate-100/60 dark:bg-slate-800/60"
              >
                {row.TaskName}
              </div>
            );
          }

          const sIdx = row.StartDate ? idxOf(row.StartDate) : -1;
          const eIdx = row.EndDate ? idxOf(row.EndDate) : -1;
          const hasBar =
            sIdx >= 0 && eIdx >= 0 && eIdx >= sIdx && ticks.length > 0;
          const isComplete = (row.Progress ?? 0) >= 100;
          const color = isComplete
            ? COMPLETE_COLOR
            : row.Color || "#f97316";

          return (
            <div
              key={row.TaskID}
              className="timeline-row grid"
              style={{
                gridTemplateColumns: colTemplate,
                position: "relative",
              }}
            >
              {ticks.map((d, i) => {
                const isWeekend = [0, 6].includes(d.getDay());
                return (
                  <div
                    key={i}
                    className={`timeline-cell h-7 ${
                      highlightWeekends && isWeekend
                        ? "timeline-weekend"
                        : ""
                    }`}
                  />
                );
              })}

              {hasBar && (
                <div
                  className="timeline-bar"
                  style={{
                    gridColumnStart: sIdx + 1,
                    gridColumnEnd: eIdx + 2,
                    gridRow: 1,
                    background: color,
                  }}
                  title={row.TaskName}
                  onClick={() => onOpen?.(row)}
                >
                  {fastMode
                    ? null
                    : row.ShortName || twoWord(row.TaskName || "")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* --------- Outline panel with inline editing --------- */
function OutlinePanel({ rows, selectedColumns, onPatch, onOpen }) {
  const isFormControl = (el) => {
    const tag = el?.tagName || "";
    return ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(tag);
  };

  const handleBlur = (row, field, value) => {
    const id = row.TaskID;
    if (!id || String(id).startsWith("grp-")) return;
    const patch = {};
    if (field === "TaskName") patch.title = value;
    if (field === "StartDate") patch.startDate = value || null;
    if (field === "EndDate") patch.endDate = value || null;
    if (field === "Progress") patch.progress = Number(value) || 0;
    if (field === "Batch") patch.batchId = value;
    if (field === "Kiln") patch.kiln = value;
    if (field === "Assignees")
      patch.assignees = String(value || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    if (field === "Notes") patch.notes = value;

    if (patch.startDate && patch.endDate) {
      const sd = d(patch.startDate);
      const ed = d(patch.endDate);
      patch.duration = Math.max(
        1,
        Math.round((ed - sd) / dayMs) + 1
      );
    }

    onPatch(id, patch);
  };

  const visibleRows = rows.flatMap((r) =>
    r.isSummary ? r.subtasks || [] : [r]
  );

  return (
    <div className="p-3 space-y-2">
      <div className="text-xs font-semibold mb-2 uppercase tracking-wide opacity-70">
        Outline
      </div>
      {visibleRows.map((row) => (
        <div
          key={row.TaskID}
          className="outline-card rounded-lg border border-slate-200/70 dark:border-slate-700/70 p-2"
          onDoubleClick={(e) => {
            if (!isFormControl(e.target)) onOpen?.(row);
          }}
        >
          <div className="space-y-1">
            {/* Title */}
            <input
              className="w-full border rounded px-2 py-1 text-xs mb-1 dark:bg-slate-900/40"
              defaultValue={row.TaskName || ""}
              onBlur={(e) =>
                handleBlur(row, "TaskName", e.target.value)
              }
              placeholder="Title"
            />
            {/* Dates */}
            <div className="flex gap-2">
              <input
                type="date"
                className="border rounded px-2 py-1 text-xs flex-1 dark:bg-slate-900/40"
                defaultValue={
                  row.StartDate ? toYMD(row.StartDate) : ""
                }
                onBlur={(e) =>
                  handleBlur(row, "StartDate", e.target.value)
                }
              />
              <input
                type="date"
                className="border rounded px-2 py-1 text-xs flex-1 dark:bg-slate-900/40"
                defaultValue={
                  row.EndDate ? toYMD(row.EndDate) : ""
                }
                onBlur={(e) =>
                  handleBlur(row, "EndDate", e.target.value)
                }
              />
            </div>
            {/* Progress */}
            <input
              type="number"
              min="0"
              max="100"
              className="w-full border rounded px-2 py-1 text-xs dark:bg-slate-900/40"
              defaultValue={row.Progress ?? 0}
              onBlur={(e) =>
                handleBlur(row, "Progress", e.target.value)
              }
              placeholder="Progress %"
            />
            {/* Batch/Kiln */}
            <div className="flex gap-2">
              <input
                className="flex-1 border rounded px-2 py-1 text-xs dark:bg-slate-900/40"
                defaultValue={row.Batch || ""}
                onBlur={(e) =>
                  handleBlur(row, "Batch", e.target.value)
                }
                placeholder="Batch"
              />
              <input
                className="flex-1 border rounded px-2 py-1 text-xs dark:bg-slate-900/40"
                defaultValue={row.Kiln || ""}
                onBlur={(e) =>
                  handleBlur(row, "Kiln", e.target.value)
                }
                placeholder="Kiln"
              />
            </div>
            {/* Assignees */}
            <input
              className="w-full border rounded px-2 py-1 text-xs dark:bg-slate-900/40"
              defaultValue={row.Assignees || ""}
              onBlur={(e) =>
                handleBlur(row, "Assignees", e.target.value)
              }
              placeholder="Assignees (comma-separated)"
            />
            {/* Notes */}
            <textarea
              className="w-full border rounded px-2 py-1 text-xs dark:bg-slate-900/40"
              defaultValue={row.Notes || ""}
              onBlur={(e) =>
                handleBlur(row, "Notes", e.target.value)
              }
              placeholder="Notes"
              rows={2}
            />
            {/* View button */}
            <div className="text-right pt-1">
              <button
                className="px-2 py-1 border rounded text-[11px]"
                onClick={() => onOpen?.(row)}
              >
                View
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
